// One filesystem's browser: a file tree (built client-side from flat paths)
// beside a viewer/editor. Markdown renders as markdown (md-prose); everything
// else shows in a code block. The list polls so files written live by agents
// (via their workspace_* tools) appear without a refresh.
import { useClient } from '@cortex/react-query';
import type {
  PersistentAgentFilesystemDetail,
  PersistentAgentFilesystemEntry,
  PersistentAgentFilesystemFile,
} from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Fragment, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { BackButton } from '../../../../components/common/BackButton';
import { ConfirmDialog } from '../../../../components/common/ConfirmDialog';
import { InlineEditableText } from '../../../../components/common/InlineEditableText';
import { Modal } from '../../../../components/common/Modal';
import { PageHeader } from '../../../../components/common/PageHeader';
import { Spinner } from '../../../../components/common/Spinner';
import { routes } from '../../../../lib/constants';
import { formatRelative } from '../../../../lib/format';

import '../../../../components/common/markdown-prose.css';

export const Route = createFileRoute('/agents/orchestration/filesystems/$filesystemId')({
  component: FilesystemPage,
});

/** One node of the client-built tree. */
interface ITreeNode {
  name: string;
  path: string;
  type: 'file' | 'dir';
  entry?: PersistentAgentFilesystemEntry;
  children: ITreeNode[];
}

/** Build a nested tree from the flat path list (dirs implicit from paths). */
export function buildTree(entries: PersistentAgentFilesystemEntry[]): ITreeNode[] {
  const root: ITreeNode = { name: '', path: '', type: 'dir', children: [] };
  const dirNode = (path: string): ITreeNode => {
    let node = root;
    if (path === '') return node;
    for (const part of path.split('/')) {
      let next = node.children.find((c) => c.name === part && c.type === 'dir');
      if (!next) {
        next = {
          name: part,
          path: node.path === '' ? part : `${node.path}/${part}`,
          type: 'dir',
          children: [],
        };
        node.children.push(next);
      }
      node = next;
    }
    return node;
  };
  for (const entry of entries) {
    if (entry.type === 'dir') {
      dirNode(entry.path);
      continue;
    }
    const slash = entry.path.lastIndexOf('/');
    const parent = dirNode(slash === -1 ? '' : entry.path.slice(0, slash));
    parent.children.push({
      name: slash === -1 ? entry.path : entry.path.slice(slash + 1),
      path: entry.path,
      type: 'file',
      entry,
      children: [],
    });
  }
  const sort = (nodes: ITreeNode[]): ITreeNode[] => {
    nodes.sort((a, b) =>
      a.type !== b.type ? (a.type === 'dir' ? -1 : 1) : a.name.localeCompare(b.name),
    );
    nodes.forEach((n) => sort(n.children));
    return nodes;
  };
  return sort(root.children);
}

const isMarkdown = (file: PersistentAgentFilesystemFile): boolean =>
  file.mimeType === 'text/markdown' || file.path.endsWith('.md');

function FilesystemPage() {
  const { filesystemId } = Route.useParams();
  const client = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [showNewFile, setShowNewFile] = useState(false);
  const [newPath, setNewPath] = useState('');
  const [newContent, setNewContent] = useState('');
  const [confirmDeleteFs, setConfirmDeleteFs] = useState(false);
  const [confirmDeletePath, setConfirmDeletePath] = useState<string | null>(null);

  const base = `v1/persistent-agent-filesystems/${filesystemId}`;

  const fsQuery = useQuery({
    queryKey: ['persistent-agent-filesystems', filesystemId],
    queryFn: () => client.http.get(base).json<PersistentAgentFilesystemDetail>(),
  });
  const filesQuery = useQuery({
    queryKey: ['persistent-agent-filesystems', filesystemId, 'files'],
    queryFn: () => client.http.get(`${base}/files`).json<PersistentAgentFilesystemEntry[]>(),
    // Poll so files written live by agents appear without a refresh.
    refetchInterval: 5_000,
  });
  const fileQuery = useQuery({
    queryKey: ['persistent-agent-filesystems', filesystemId, 'file', selected],
    queryFn: () =>
      client.http
        .get(`${base}/file`, { searchParams: { path: selected ?? '' } })
        .json<PersistentAgentFilesystemFile>(),
    enabled: selected !== null,
    refetchInterval: editing ? false : 5_000,
  });

  const rename = useMutation({
    mutationFn: (name: string) => client.http.patch(base, { json: { name } }).json(),
    onSuccess: () => fork(qc.invalidateQueries({ queryKey: ['persistent-agent-filesystems'] })),
  });
  const saveFile = useMutation({
    mutationFn: (args: { path: string; content: string }) =>
      client.http.put(`${base}/file`, { json: args }).json<PersistentAgentFilesystemFile>(),
    onSuccess: async (saved) => {
      setEditing(false);
      setShowNewFile(false);
      setNewPath('');
      setNewContent('');
      setSelected(saved.path);
      await qc.invalidateQueries({ queryKey: ['persistent-agent-filesystems', filesystemId] });
    },
  });
  const deletePath = useMutation({
    mutationFn: (path: string) =>
      client.http.delete(`${base}/file`, { searchParams: { path } }).text(),
    onSuccess: async (_out, path) => {
      setConfirmDeletePath(null);
      if (selected === path || selected?.startsWith(`${path}/`)) setSelected(null);
      await qc.invalidateQueries({ queryKey: ['persistent-agent-filesystems', filesystemId] });
    },
  });
  const deleteFs = useMutation({
    mutationFn: () => client.http.delete(base).text(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['persistent-agent-filesystems'] });
      fork(navigate({ to: routes.ORCHESTRATION_FILESYSTEMS }));
    },
  });

  const tree = useMemo(() => buildTree(filesQuery.data ?? []), [filesQuery.data]);
  const fs = fsQuery.data;
  const file = fileQuery.data;

  const toggleDir = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const renderNodes = (nodes: ITreeNode[], depth: number) => (
    <Fragment>
      {nodes.map((node) => (
        <Fragment key={node.path}>
          <button
            className="btn btn--ghost btn--sm"
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-start',
              gap: 6,
              paddingLeft: 8 + depth * 14,
              fontWeight: node.path === selected ? 600 : 400,
              background: node.path === selected ? 'var(--surface-active)' : undefined,
            }}
            onClick={() => (node.type === 'dir' ? toggleDir(node.path) : setSelected(node.path))}
            title={node.path}
          >
            <span aria-hidden={true} style={{ opacity: 0.7 }}>
              {node.type === 'dir' ? (collapsed.has(node.path) ? '▸' : '▾') : '·'}
            </span>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {node.name}
            </span>
          </button>
          {node.type === 'dir' &&
            !collapsed.has(node.path) &&
            renderNodes(node.children, depth + 1)}
        </Fragment>
      ))}
    </Fragment>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader
        title={
          fs ? (
            <InlineEditableText
              value={fs.name}
              ariaLabel="Filesystem name"
              isPending={rename.isPending}
              validate={(next) => (next.length === 0 ? 'Name is required' : null)}
              onSubmit={(next) => rename.mutate(next)}
              textStyle={{ fontWeight: 600, fontSize: 'var(--fs-lg)' }}
            />
          ) : (
            'Filesystem'
          )
        }
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <BackButton to={routes.ORCHESTRATION_FILESYSTEMS} label="Filesystems" />
            {fs && fs.attachedGroups.length > 0 && (
              <span
                className="t-xs t-muted"
                title={`Attached to: ${fs.attachedGroups.map((g) => g.name).join(', ')}`}
              >
                shared by{' '}
                {fs.attachedGroups.map((g) => (
                  <span key={g.id} className="badge badge--neutral" style={{ marginLeft: 4 }}>
                    {g.name}
                  </span>
                ))}
              </span>
            )}
            <button className="btn btn--secondary btn--sm" onClick={() => setShowNewFile(true)}>
              New file
            </button>
            <button
              className="btn btn--danger btn--sm"
              disabled={deleteFs.isPending}
              onClick={() => setConfirmDeleteFs(true)}
            >
              Delete filesystem
            </button>
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
        <div
          className="card"
          style={{
            width: 280,
            flexShrink: 0,
            overflowY: 'auto',
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {filesQuery.isLoading && <Spinner />}
          {!filesQuery.isLoading && tree.length === 0 && (
            <p className="t-sm t-muted" style={{ margin: 8 }}>
              No files yet — create one, or let an attached agent write the first.
            </p>
          )}
          {renderNodes(tree, 0)}
        </div>

        <div className="card" style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: 16 }}>
          {!selected && (
            <p className="t-sm t-muted" style={{ margin: 0 }}>
              Select a file to view it.
            </p>
          )}
          {selected && fileQuery.isLoading && <Spinner />}
          {selected && file && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <code className="t-sm" style={{ fontWeight: 600 }}>
                  {file.path}
                </code>
                <span className="t-xs t-muted">
                  {file.size} B · updated {formatRelative(file.updatedAt)}
                  {file.updatedByType && (
                    <>
                      {' '}
                      by {file.updatedByType === 'agent' ? 'agent ' : ''}
                      <code>{file.updatedById?.slice(0, 12)}</code>
                    </>
                  )}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  {!editing && (
                    <button
                      className="btn btn--secondary btn--sm"
                      onClick={() => {
                        setDraft(file.content);
                        setEditing(true);
                      }}
                    >
                      Edit
                    </button>
                  )}
                  <button
                    className="btn btn--ghost btn--sm"
                    style={{ color: 'var(--status-danger)' }}
                    onClick={() => setConfirmDeletePath(file.path)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <textarea
                    className="input"
                    style={{ minHeight: 360, fontFamily: 'var(--font-mono, monospace)' }}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={saveFile.isPending}
                      onClick={() => setEditing(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn--primary btn--sm"
                      disabled={saveFile.isPending}
                      onClick={() => saveFile.mutate({ path: file.path, content: draft })}
                    >
                      {saveFile.isPending ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {saveFile.isError && (
                    <div className="alert alert--danger">
                      {String((saveFile.error as Error)?.message ?? 'Failed to save')}
                    </div>
                  )}
                </div>
              ) : isMarkdown(file) ? (
                <div className="md-prose">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{file.content}</ReactMarkdown>
                </div>
              ) : (
                <pre
                  className="t-sm"
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'var(--font-mono, monospace)',
                  }}
                >
                  {file.content}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={showNewFile}
        onClose={() => {
          if (!saveFile.isPending) setShowNewFile(false);
        }}
        title="New file"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              className="btn btn--ghost btn--sm"
              disabled={saveFile.isPending}
              onClick={() => setShowNewFile(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={saveFile.isPending || newPath.trim().length === 0}
              onClick={() => saveFile.mutate({ path: newPath.trim(), content: newContent })}
            >
              {saveFile.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label className="t-sm" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Path
            <input
              className="input"
              value={newPath}
              onChange={(e) => setNewPath(e.target.value)}
              placeholder="notes/handover.md"
              autoFocus={true}
            />
          </label>
          <label className="t-sm" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Content
            <textarea
              className="input"
              style={{ minHeight: 200, fontFamily: 'var(--font-mono, monospace)' }}
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="# Notes…"
            />
          </label>
          {saveFile.isError && (
            <div className="alert alert--danger">
              {String((saveFile.error as Error)?.message ?? 'Failed to create file')}
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDeletePath !== null}
        onClose={() => {
          if (!deletePath.isPending) setConfirmDeletePath(null);
        }}
        onConfirm={() => confirmDeletePath && deletePath.mutate(confirmDeletePath)}
        title="Delete file"
        message={`Delete '${confirmDeletePath ?? ''}'? This cannot be undone.`}
        confirmLabel={deletePath.isPending ? 'Deleting…' : 'Delete'}
        destructive={true}
        pending={deletePath.isPending}
      />

      <ConfirmDialog
        open={confirmDeleteFs}
        onClose={() => {
          if (!deleteFs.isPending) setConfirmDeleteFs(false);
        }}
        onConfirm={() => deleteFs.mutate()}
        eyebrow={fs?.name}
        title="Delete filesystem"
        message="Detaches it from every persistent agent (their threads lose the file tools on their next build — i.e. after a reset). Files are retained but no longer reachable."
        confirmLabel={deleteFs.isPending ? 'Deleting…' : 'Delete filesystem'}
        destructive={true}
        pending={deleteFs.isPending}
      />
    </div>
  );
}
